import { AxiomReportType } from '@/enums/axiom-report.enum'
import { RunAndScheduleType } from '@/enums/scenario-builder.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import { refreshPortfolioAndBenchmarkLists } from '@/hooks/usePortfolioAndBenchmarkLists'
import {
  FormState,
  TPortfolio,
  TReportTemplate,
} from '@/interfaces/scenarioBuilder.interface'
import {
  getPortfolioDates,
  getReportTemplates,
  runReportTemplate2,
  scheduleReportTemplate2,
} from '@/services/scenarioBuilder.service'
import { toggleRunReportTemplateModalOpened } from '@/states/scenarioBuilder.slice'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import ReportTypeDropdown from '../ReportType/ReportType'
import LoadingButton from '../Shared/LoadingButton'

type TScheduleFrequency = 'year' | 'month' | 'week' | 'day'

// GFR, GFPA, MENU and RV reports always use the hard-coded POSTGRES template.
const POSTGRES_TEMPLATE_NAME = 'POSTGRES'

const usesPostgresTemplate = (reportType: AxiomReportType): boolean =>
  reportType === AxiomReportType.GFR ||
  reportType === AxiomReportType.GFPA ||
  reportType === AxiomReportType.MENU ||
  reportType === AxiomReportType.RV

// GFPA and RV Run Now use a begin/end date range (instead of a single as-of
// date) and their scheduling only supports Daily/Weekly/Monthly frequencies.
const usesBeginEndDateRange = (reportType: AxiomReportType): boolean =>
  reportType === AxiomReportType.GFPA || reportType === AxiomReportType.RV

export const RunScenarioModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const isRunReportTemplateModalOpened = useAppSelector(
    (s) => s.scenarioBuilder.isRunReportTemplateModalOpened,
  )
  const userInfo = useAppSelector((s) => s.scenarioBuilder.userInfo)
  const portfolioList = useAppSelector((s) => s.scenarioBuilder.portfolioList)
  const benchmarkList = useAppSelector((s) => s.scenarioBuilder.benchmarkList)
  const portfolioListLoading = useAppSelector(
    (s) => s.scenarioBuilder.portfolioListLoading,
  )
  const benchmarkListLoading = useAppSelector(
    (s) => s.scenarioBuilder.benchmarkListLoading,
  )
  const selectedTemplateReport = useAppSelector(
    (s) => s.scenarioBuilder.selectedReportTemplate,
  )

  const [formData, setFormData] = useState<FormState>({
    reportType: selectedTemplateReport?.reportType || AxiomReportType.SCEN,
    portfolio: {
      name: '',
      description: '',
      type: '',
      benchmarkName: '',
      benchmarkDescription: '',
      dates: [],
    },
    benchmark: {
      apiName: '',
      description: '',
      userNumber: '',
      vendor: '',
    },
    template: '',
    matrix: '',
  })
  const [asOfDate, setAsOfDate] = useState<Date | null>(null)
  const [beginDate, setBeginDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [dateError, setDateError] = useState(false)
  const [runAndScheduleOption, setRunAndScheduleOption] =
    useState<RunAndScheduleType>(RunAndScheduleType.RUN)
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null)
  const [scheduleTime, setScheduleTime] = useState<Date | null>(null)
  const [scheduleFrequency, setScheduleFrequency] =
    useState<TScheduleFrequency>('day')
  const [reportTemplates, setReportTemplates] = useState<TReportTemplate[]>([])
  const [templateLoading, setTemplateLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [portfolioDates, setPortfolioDates] = useState(false)
  const [templateDataForApi, setTemplateDataForApi] = useState<
    TReportTemplate | undefined
  >(undefined)
  const [selectedPortfolio, setSelectedPortfolio] = useState<
    TPortfolio | undefined
  >(undefined)
  const [isRefreshingLists, setIsRefreshingLists] = useState(false)

  const username = userInfo?.name || ''
  const usesDateRange = usesBeginEndDateRange(formData.reportType)

  const handleRefreshPortfolioAndBenchmarkLists = async () => {
    setIsRefreshingLists(true)
    try {
      const succeeded = await refreshPortfolioAndBenchmarkLists(dispatch)
      if (!succeeded) {
        toast.error(
          'Unable to refresh Portfolio and Benchmark data. Please try again later.',
        )
      }
    } finally {
      setIsRefreshingLists(false)
    }
  }

  const getScheduleFrequencyValue = (value: TScheduleFrequency): number => {
    switch (value) {
      case 'year':
        return 1
      case 'month':
        return 2
      case 'week':
        return 3
      case 'day':
      default:
        return 4
    }
  }

  const getStartingDateTimeIso = (): string | null => {
    if (!scheduleDate || !scheduleTime) return null
    const d = dayjs(scheduleDate)
    const t = dayjs(scheduleTime)
    return d
      .hour(t.hour())
      .minute(t.minute())
      .second(0)
      .millisecond(0)
      .utc()
      .toISOString()
  }

  // to handle the dates for the portfolio
  const validDates =
    selectedPortfolio && selectedPortfolio.dates
      ? selectedPortfolio.dates.map(
          (dateString: string | number | Date) => new Date(dateString),
        )
      : []

  const isValidDate = (date: Date) => {
    return validDates.some(
      (validDate) => validDate.toDateString() === date.toDateString(),
    )
  }

  // handlers
  const closeDialog = () => {
    dispatch(toggleRunReportTemplateModalOpened(false))
  }

  const isFormEmpty = () => {
    const { portfolio, benchmark, template } = formData

    const isPortfolioNameEmpty = portfolio.name === ''
    const isBenchmarkApiNameEmpty = benchmark && benchmark.apiName === ''
    const isTemplateEmpty = template === ''

    const isRunAndScheduleInvalid =
      runAndScheduleOption === RunAndScheduleType.RUN
        ? usesDateRange
          ? beginDate === null ||
            endDate === null ||
            dateError ||
            beginDate >= endDate
          : asOfDate === null
        : scheduleDate === null || scheduleTime === null

    return (
      isPortfolioNameEmpty ||
      isBenchmarkApiNameEmpty ||
      isTemplateEmpty ||
      isRunAndScheduleInvalid
    )
  }

  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRunAndScheduleOption(e.target.value as RunAndScheduleType)
  }

  const handleRunNow = async () => {
    if (runAndScheduleOption === RunAndScheduleType.RUN) {
      setLoading(true)
      try {
        /// V1 Scheduling
        const runReport2Payload = {
          username,
          is_enabled: true,
          report_type: usesPostgresTemplate(formData.reportType)
            ? formData.reportType
            : templateDataForApi?.reportType ||
              selectedTemplateReport?.reportType ||
              AxiomReportType.SCEN,
          portfolio_name: formData.portfolio.name,
          benchmark: formData.benchmark && formData.benchmark.apiName,
          template_id: usesPostgresTemplate(formData.reportType)
            ? POSTGRES_TEMPLATE_NAME
            : templateDataForApi?.id || 'ERROR',
          frequency: 0, // RunNow
          timezone: 'UTC',
          starting_datetime: usesDateRange
            ? dayjs(endDate).utc().toISOString()
            : dayjs(asOfDate).utc().toISOString(),
          ...(usesDateRange && {
            begin_date: dayjs(beginDate).format('YYYY-MM-DD'),
            end_date: dayjs(endDate).format('YYYY-MM-DD'),
          }),
        }
        // const runReport2PayloadOld = {
        //   reportType:
        //     templateDataForApi?.reportType ||
        //     selectedTemplateReport?.reportType ||
        //     AxiomReportType.SCEN,
        //   portfolioName: formData.portfolio.name,
        //   benchmark: formData.benchmark && formData.benchmark.apiName,
        //   templateName: formData.template,
        //   analysisDate: +dayjs(asOfDate).format(DATE_FORMAT.API_DATE_TIME),
        //   hedged:
        //     templateDataForApi &&
        //     templateDataForApi.hedgingParam.charAt(0).toUpperCase(),
        //   hedgedPercent: templateDataForApi && templateDataForApi.hedgedValue,
        // }
        await runReportTemplate2(runReport2Payload)
        /// End of V2 Scheduling
        toast.success('Report Run Successfully')
        setTimeout(() => {
          dispatch(toggleRunReportTemplateModalOpened(false))
        }, 500)
      } catch (error) {
        console.error('There was a problem with the fetch operation:', error)
        toast.error(
          'Unable to create report. Please reach out to your servicing team for support',
        )
        setTimeout(() => {
          dispatch(toggleRunReportTemplateModalOpened(false))
        }, 500)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(true)
      try {
        /// V1 Scheduling
        // const scheduleReportPayload = {
        //   requestPayload: {
        //     reportType: templateDataForApi?.reportType || AxiomReportType.SCEN,
        //     templateName: templateDataForApi && templateDataForApi.name,
        //     templateId: templateDataForApi && templateDataForApi.id,
        //     portfolioId: 'string',
        //     portfolioName: formData.portfolio.name,
        //     benchmark: formData.benchmark && formData.benchmark.apiName,
        //     hedged:
        //       templateDataForApi &&
        //       templateDataForApi.hedgingParam.charAt(0).toUpperCase(),
        //     hedgedPercent: templateDataForApi && templateDataForApi.hedgedValue,
        //   },
        //   schedule: {
        //     frequency: frequency,
        //     timezone: selectedTimeZone,
        //     dayOfMonth: dayOfMonth,
        //     dayOfWeek: dayOfWeek,
        //     month: month,
        //     minute: minute,
        //     hour: hour,
        //   },
        //   enabled: true,
        // }
        // console.log('scheduleReportPayload', scheduleReportPayload)
        // console.log('Not calling the new API right now')
        // await scheduleReportTemplate(scheduleReportPayload)
        /// End V1 Scheduling

        /// V2 Scheduling
        const startingDateTimeIso = getStartingDateTimeIso()
        if (!startingDateTimeIso) {
          throw new Error('Missing schedule date/time')
        }
        const runReport2Payload = {
          username,
          is_enabled: true,
          report_type: usesPostgresTemplate(formData.reportType)
            ? formData.reportType
            : templateDataForApi?.reportType ||
              selectedTemplateReport?.reportType ||
              AxiomReportType.SCEN,
          portfolio_name: formData.portfolio.name,
          benchmark: formData.benchmark && formData.benchmark.apiName,
          template_id: usesPostgresTemplate(formData.reportType)
            ? POSTGRES_TEMPLATE_NAME
            : templateDataForApi?.id || 'ERROR',
          frequency: getScheduleFrequencyValue(scheduleFrequency),
          timezone: 'UTC',
          starting_datetime: startingDateTimeIso,
        }
        await scheduleReportTemplate2(runReport2Payload)
        /// End V2 Scheduling
        toast.success('Report Scheduled Successfully')
        setTimeout(() => {
          dispatch(toggleRunReportTemplateModalOpened(false))
        }, 500)
      } catch (error) {
        console.error('There was a problem with the fetch operation:', error)
        toast.error('Something went Wrong')
        setTimeout(() => {
          dispatch(toggleRunReportTemplateModalOpened(false))
        }, 500)
      } finally {
        setLoading(false)
      }
    }
  }
  /* API Calling */

  const fetchPortfolioDates = async (portfolioName: string) => {
    setPortfolioDates(true)
    try {
      const response = await getPortfolioDates(portfolioName)
      setSelectedPortfolio(response)
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error)
      toast.error(
        'Unable to load valid dates for the selected portfolio. Please try again.',
      )
    } finally {
      setPortfolioDates(false)
    }
  }

  useEffect(() => {
    if (isRunReportTemplateModalOpened) {
      const fetchReportTemplates = async () => {
        setTemplateLoading(true)
        try {
          const reportTemplatesData = await getReportTemplates()
          if (reportTemplatesData?.length) {
            setReportTemplates(reportTemplatesData)
          }
        } catch (err) {
          console.warn(err)
        } finally {
          setTemplateLoading(false)
        }
      }
      fetchReportTemplates()
    }
  }, [isRunReportTemplateModalOpened])

  // Reset picked date(s) when the portfolio changes: a stale begin/end
  // range (or as-of date) from the previous portfolio may not satisfy the
  // new portfolio's validDates, and nothing else re-validates them before
  // submission. Clearing asOfDate here lets the prefill effect below
  // re-populate it against the new portfolio's valid dates.
  useEffect(() => {
    setBeginDate(null)
    setEndDate(null)
    setAsOfDate((prev) => (prev && isValidDate(prev) ? prev : null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio])

  // To pre-fill the date picker with the latest valid date for the respective selected portfolio
  useEffect(() => {
    if (validDates && validDates.length > 0 && !asOfDate) {
      setAsOfDate(validDates[validDates.length - 1])
    }
  }, [validDates, asOfDate])

  // GFPA/RV scheduling only supports Daily/Weekly/Monthly; fall back off Yearly.
  useEffect(() => {
    if (usesDateRange && scheduleFrequency === 'year') {
      setScheduleFrequency('day')
    }
  }, [usesDateRange, scheduleFrequency])

  useEffect(() => {
    if (isRunReportTemplateModalOpened && selectedTemplateReport) {
      setFormData((prev) => ({
        ...prev,
        template: selectedTemplateReport.id,
      }))
      setTemplateDataForApi(selectedTemplateReport)
    }
  }, [isRunReportTemplateModalOpened, selectedTemplateReport])

  return (
    <>
      <Dialog
        data-cy='run-scenario-modal'
        fullWidth
        maxWidth='sm'
        open={isRunReportTemplateModalOpened}
        onClose={closeDialog}
        aria-labelledby='max-width-dialog-title'
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography data-cy='run-scenario-modal-title' variant='h5'>
            Create Report
          </Typography>
          <Tooltip title='Refresh Portfolio and Benchmark Data'>
            <span>
              <IconButton
                data-cy='refresh-portfolio-benchmark-button'
                aria-label='Refresh Portfolio and Benchmark Data'
                onClick={handleRefreshPortfolioAndBenchmarkLists}
                disabled={
                  isRefreshingLists ||
                  portfolioListLoading ||
                  benchmarkListLoading
                }
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </DialogTitle>
        {(templateLoading ||
          portfolioListLoading ||
          benchmarkListLoading ||
          loading) && <LinearProgress />}
        <Divider />
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',

            gap: '1rem',
            justifyContent: 'flex-start',
          }}
        >
          <ReportTypeDropdown
            value={formData.reportType}
            onChange={(reportType) => {
              setFormData((prev) => ({
                ...prev,
                reportType,
                // GFR/GFPA/MENU/RV are hard set to the POSTGRES template; otherwise
                // clear the selected template when the report type changes.
                template: usesPostgresTemplate(reportType)
                  ? POSTGRES_TEMPLATE_NAME
                  : '',
              }))
              setTemplateDataForApi(undefined)
            }}
          />
          {usesPostgresTemplate(formData.reportType) ? (
            <TextField
              label='Report Template'
              variant='outlined'
              size='small'
              value={POSTGRES_TEMPLATE_NAME}
              disabled
            />
          ) : (
            <Autocomplete
              options={reportTemplates.filter(
                (template) => template.reportType === formData.reportType,
              )}
              disabled={templateLoading}
              getOptionLabel={(option) => option.name}
              value={
                reportTemplates.find(
                  (template) => template.id === formData.template,
                ) || null
              }
              noOptionsText='No Report Template available'
              onChange={(e, selectedTemplate) => {
                if (selectedTemplate) {
                  const filteredTemplate = reportTemplates.find(
                    (template) => template.name === selectedTemplate.name,
                  )
                  setFormData((prev) => ({
                    ...prev,
                    template: selectedTemplate.id,
                  }))
                  setTemplateDataForApi(filteredTemplate)
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Report Template'
                  variant='outlined'
                  size='small'
                />
              )}
            />
          )}
          <FormControl variant='outlined' size='small'>
            <Autocomplete
              options={portfolioList}
              getOptionLabel={(option) => option.name}
              disabled={portfolioListLoading}
              noOptionsText='No Portfolio available'
              onChange={(e, newPortfolio) => {
                if (newPortfolio) {
                  setFormData((prev) => ({
                    ...prev,
                    portfolio: newPortfolio,
                    benchmark: {
                      apiName: newPortfolio.benchmarkName,
                      description: newPortfolio.benchmarkDescription,
                    },
                  }))
                  // Clear the previous portfolio's dates immediately so a failed fetch below can't
                  // leave them paired with the newly-selected portfolio (see the reset effect keyed
                  // on selectedPortfolio).
                  setSelectedPortfolio(undefined)
                  fetchPortfolioDates(newPortfolio.name as string)
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Portfolio'
                  variant='outlined'
                  size='small'
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.name}>
                  <Box style={{ display: 'flex', gap: '8px' }}>
                    <Chip size='small' label={option.name} />
                    {option.description}
                  </Box>
                </li>
              )}
            />
          </FormControl>
          <>
            <FormControl variant='outlined' size='small'>
              <Autocomplete
                options={benchmarkList}
                getOptionLabel={(option) => option.apiName}
                disabled={benchmarkListLoading}
                value={formData.benchmark}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    benchmark: newValue,
                  }))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Benchmark'
                    variant='outlined'
                    size='small'
                  />
                )}
                renderOption={(props, option, index) => (
                  <li {...props} key={index.index}>
                    {option.apiName}
                  </li>
                )}
                noOptionsText='No Benchmark available'
              />
            </FormControl>
          </>

          <FormControl>
            <RadioGroup
              data-cy='run-schedule-radio-group'
              aria-labelledby='demo-radio-buttons-group-label'
              value={runAndScheduleOption}
              onChange={handleOptionChange}
              name='radio-buttons-group'
              sx={{ gap: 1 }}
            >
              <>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <FormControlLabel
                      data-cy='run-now-radio'
                      value={RunAndScheduleType.RUN}
                      control={<Radio />}
                      label={RunAndScheduleType.RUN}
                      sx={{ width: '15ch' }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      {usesDateRange ? (
                        <Box style={{ display: 'flex', gap: '8px' }}>
                          <FormControl
                            sx={{ width: '160px' }}
                            variant='outlined'
                            size='medium'
                            disabled={portfolioDates}
                            error={
                              runAndScheduleOption ===
                                RunAndScheduleType.RUN && dateError
                            }
                          >
                            <DatePicker
                              data-cy='begin-date-picker'
                              label='Begin Date'
                              value={beginDate}
                              onChange={(newValue, context) => {
                                setBeginDate(newValue)
                                setDateError(!!context.validationError)
                              }}
                              shouldDisableDate={(date) => !isValidDate(date)}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  error: dateError,
                                },
                              }}
                              maxDate={endDate ?? undefined}
                              disabled={
                                runAndScheduleOption ===
                                  RunAndScheduleType.SCHEDULE || portfolioDates
                              }
                            />
                          </FormControl>
                          <FormControl
                            sx={{ width: '160px' }}
                            variant='outlined'
                            size='medium'
                            disabled={portfolioDates}
                            error={
                              runAndScheduleOption ===
                                RunAndScheduleType.RUN && dateError
                            }
                          >
                            <DatePicker
                              data-cy='end-date-picker'
                              label='End Date'
                              value={endDate}
                              onChange={(newValue, context) => {
                                setEndDate(newValue)
                                setDateError(!!context.validationError)
                              }}
                              shouldDisableDate={(date) => !isValidDate(date)}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  error: dateError,
                                },
                              }}
                              minDate={beginDate ?? undefined}
                              disabled={
                                runAndScheduleOption ===
                                  RunAndScheduleType.SCHEDULE || portfolioDates
                              }
                            />
                          </FormControl>
                        </Box>
                      ) : (
                        <FormControl
                          sx={{
                            width: '200px',
                          }}
                          variant='outlined'
                          size='medium'
                          disabled={portfolioDates}
                          error={
                            runAndScheduleOption === RunAndScheduleType.RUN &&
                            dateError
                          }
                        >
                          <DatePicker
                            data-cy='as-of-date-picker'
                            label='As of Date'
                            value={asOfDate}
                            onChange={(newValue: Date | null) => {
                              setAsOfDate(newValue)
                              setDateError(false)
                            }}
                            shouldDisableDate={(date) => !isValidDate(date)}
                            slotProps={{
                              textField: {
                                size: 'small',
                                error: dateError,
                              },
                            }}
                            disabled={
                              runAndScheduleOption ===
                                RunAndScheduleType.SCHEDULE || portfolioDates
                            }
                          />
                        </FormControl>
                      )}
                    </LocalizationProvider>
                  </Box>
                  {portfolioDates && <CircularProgress size={24} />}
                </Box>
              </>

              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}
                data-cy='schedule-radio-group'
              >
                <FormControlLabel
                  data-cy='schedule-radio'
                  value={RunAndScheduleType.SCHEDULE}
                  control={<Radio />}
                  label={RunAndScheduleType.SCHEDULE}
                  sx={{ width: '15ch' }}
                  disabled={formData.reportType === AxiomReportType.GFPAH}
                />

                {runAndScheduleOption === RunAndScheduleType.SCHEDULE && (
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: '8px',
                    }}
                  >
                    <TextField
                      select
                      label='Frequency'
                      size='small'
                      value={scheduleFrequency}
                      onChange={(e) =>
                        setScheduleFrequency(
                          e.target.value as TScheduleFrequency,
                        )
                      }
                    >
                      {!usesDateRange && (
                        <MenuItem value='year'>Yearly</MenuItem>
                      )}
                      <MenuItem value='month'>Monthly</MenuItem>
                      <MenuItem value='week'>Weekly</MenuItem>
                      <MenuItem value='day'>Daily</MenuItem>
                    </TextField>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label='Start Date'
                        value={scheduleDate}
                        onChange={(newValue: Date | null) =>
                          setScheduleDate(newValue)
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                          },
                        }}
                      />
                      <TimePicker
                        label='Start Time'
                        value={scheduleTime}
                        onChange={(newValue: Date | null) =>
                          setScheduleTime(newValue)
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              </Box>
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <Divider />

        <DialogActions>
          <LoadingButton
            data-cy='run-scenario-submit-button'
            loading={loading}
            type='submit'
            onClick={() => handleRunNow()}
            color='primary'
            disabled={isFormEmpty()}
          >
            Submit
          </LoadingButton>
          <Button
            data-cy='run-scenario-cancel-button'
            onClick={() => {
              dispatch(toggleRunReportTemplateModalOpened(false))
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
